import { AsyncPipe, CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { animate, AnimationEvent, state, style, transition, trigger } from '@angular/animations';
import { NgLetModule } from 'ng-let';
import { combineLatest, distinctUntilChanged } from 'rxjs';
import { AiChatConversationService, AiChatThreadService } from '../conversation-tools';
import { AiChatTriggerButtonComponent, AiChatUiEventsService, AiChatWindowComponent } from '@panaya/ai-chat/ui';
import { AiChatWindowContentComponent } from '../ai-chat-window-content';
import { AiChatLayoutService } from '../ai-chat-layout.service';
import { CanvasFrameStore } from '../canvas/canvas-frame.store';
import { AiChatLayout } from '@panaya/ai-chat/shared';
import { injectWidgetConfigProp } from '@panaya/ai-chat/utils';
import { CdkDrag } from '@angular/cdk/drag-drop';
import { OverlayContainer } from '@angular/cdk/overlay';

@Component({
    standalone: true,
    selector: 'p-ai-chat-widget',
    imports: [
        CommonModule,
        NgLetModule,
        AsyncPipe,
        AiChatTriggerButtonComponent,
        AiChatWindowComponent,
        AiChatWindowContentComponent,
        CdkDrag
    ],
    templateUrl: './ai-chat-widget.component.html',
    styleUrls: ['./ai-chat-widget.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[class.top-most]': 'topMost'
    },
    animations: [
        trigger('floatSlide', [
            state('floating', style({ transform: 'translateX(0)', opacity: 1 })),
            state('sidebar', style({ transform: 'translateX(120%)', opacity: 0 })),
            transition('floating => sidebar', animate('400ms cubic-bezier(0.4, 0, 0.2, 1)')),
            transition('sidebar => floating', animate('400ms cubic-bezier(0.4, 0, 0.2, 1)'))
            // No :enter/:leave transitions — the window appears instantly when a new thread starts
        ])
    ]
})
export class AiChatWidgetComponent {
    @Input() isDraggable = false;
    @Input() draggableBoundaryClass = '';
    @Input() topMost = true;

    private readonly conversation = inject(AiChatConversationService);
    private readonly layoutService = inject(AiChatLayoutService);
    protected readonly threadService = inject(AiChatThreadService);
    protected readonly layout$ = this.layoutService.layout$;
    protected readonly frameStore = inject(CanvasFrameStore);
    protected readonly aiChatWindowCustomClass = injectWidgetConfigProp('aiChatWindowCustomClass');
    protected readonly assistantWorking$ = this.conversation.assistantWorking;

    private readonly events = inject(AiChatUiEventsService);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly destroyRef = inject(DestroyRef);
    private readonly overlayContainer = inject(OverlayContainer);

    protected showFloat = false;
    protected currentLayout: AiChatLayout = 'floating';

    private destroyed = false;

    constructor() {
        this.destroyRef.onDestroy(() => {
            this.destroyed = true;
        });
        this.subscribeToFloatVisibility();
    }

    protected onFloatAnimDone(event: AnimationEvent): void {
        if (this.destroyed || event.toState !== 'sidebar') {
            return;
        }
        this.showFloat = false;
        this.cdr.markForCheck();
    }

    protected setLayout(layout: AiChatLayout): void {
        this.layoutService.setLayout(layout);
    }

    protected threadEventChanged(action: 'init' | 'close'): void {
        if (action === 'init') {
            this.events.triggerInitThreadRequest();
        } else {
            this.events.closeThreadRequest.emit();
        }
    }

    protected get draggableBoundarySelector(): string {
        return this.draggableBoundaryClass ? `.${this.draggableBoundaryClass}` : '';
    }

    private subscribeToFloatVisibility(): void {
        combineLatest([this.layout$, this.threadService.isActive])
            .pipe(
                distinctUntilChanged(([l1, a1], [l2, a2]) => l1 === l2 && a1 === a2),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(([layout, active]) => this.updateFloatState(layout, active));
    }

    private updateFloatState(layout: AiChatLayout, active: boolean): void {
        if (layout === 'floating' && active) {
            this.showFloat = true;
            this.currentLayout = 'floating';
            this.overlayContainer.getContainerElement().style.zIndex = '100001';
        } else if (this.showFloat) {
            this.currentLayout = 'sidebar';
            this.overlayContainer.getContainerElement().style.zIndex = '';
        } else {
            this.showFloat = false;
        }
        this.cdr.markForCheck();
    }
}
